import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiPencilOutline, mdiDeleteOutline } from "@mdi/js";
import { Button, Chip, Spinner, Table } from "@heroui/react";
import type { GasStationResponseDto } from "@pro-sto/api-client";
import { gasStationsStore } from "@/stores/GasStationsStore";
import {
  getStatusLabel,
  parseGasStationField,
  type GasStationWithAdminOverride,
} from "../constants";

const STATUS_CHIP_COLOR: Record<
  GasStationResponseDto["status"],
  "success" | "warning" | "danger" | "default"
> = {
  yes: "success",
  queue: "warning",
  low: "warning",
  no: "danger",
};

interface Column {
  id: string;
  label: string;
  width?: string;
  isRowHeader?: boolean;
  render: (station: GasStationResponseDto) => React.ReactNode;
}

interface GasStationsTableProps {
  onEdit: (uuid: string) => void;
}

const GasStationsTable: React.FC<GasStationsTableProps> = observer(
  ({ onEdit }) => {
    const { filteredStations, isLoading, search } = gasStationsStore;

    const columns: Column[] = [
      {
        id: "name",
        label: "Название",
        isRowHeader: true,
        width: "200px",
        render: (station) => {
          const { admin_override: adminOverride } =
            station as GasStationWithAdminOverride;
          return (
            <div className="gs-page__name-cell">
              <span>{station.name}</span>
              {adminOverride && (
                <Chip size="sm" variant="soft" color="accent">
                  Админ
                </Chip>
              )}
            </div>
          );
        },
      },
      {
        id: "brand",
        label: "Бренд",
        width: "140px",
        render: (station) => parseGasStationField(station.brand) || "—",
      },
      {
        id: "address",
        label: "Адрес",
        render: (station) => station.address || "—",
      },
      {
        id: "status",
        label: "Статус",
        width: "130px",
        render: (station) => (
          <Chip
            size="sm"
            variant="soft"
            color={STATUS_CHIP_COLOR[station.status]}
          >
            {getStatusLabel(station.status)}
          </Chip>
        ),
      },
      {
        id: "fuels",
        label: "Топливо",
        width: "160px",
        render: (station) => station.fuels_now || "—",
      },
      {
        id: "confirmations",
        label: "Подтверждения",
        width: "130px",
        render: (station) => station.confirmations,
      },
      {
        id: "actions",
        label: "Действия",
        width: "100px",
        render: (station) => (
          <div className="gs-page__actions">
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              aria-label="Редактировать"
              onPress={() => onEdit(station.uuid)}
            >
              <Icon path={mdiPencilOutline} size={0.75} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              aria-label="Удалить"
              onPress={() => gasStationsStore.requestDelete(station)}
            >
              <Icon path={mdiDeleteOutline} size={0.75} />
            </Button>
          </div>
        ),
      },
    ];

    if (isLoading && filteredStations.length === 0) {
      return (
        <div className="gs-page__loading">
          <Spinner size="lg" />
        </div>
      );
    }

    if (!isLoading && filteredStations.length === 0) {
      return (
        <div className="gs-page__empty">
          {search.trim()
            ? "Заправки не найдены"
            : "Список заправок пуст. Добавьте первую заправку."}
        </div>
      );
    }

    return (
      <div className="gs-page__table-wrap">
        <Table>
          <Table.ScrollContainer>
            <Table.Content
              aria-label="Заправки"
              className="gs-page__table"
              style={{ minWidth: "960px" }}
            >
              <Table.Header>
                {columns.map((col) => (
                  <Table.Column
                    key={col.id}
                    id={col.id}
                    isRowHeader={col.isRowHeader}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </Table.Column>
                ))}
              </Table.Header>
              <Table.Body>
                {filteredStations.map((station) => (
                  <Table.Row key={station.uuid} id={station.uuid}>
                    {columns.map((col) => (
                      <Table.Cell key={col.id}>{col.render(station)}</Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>
    );
  },
);

GasStationsTable.displayName = "GasStationsTable";
export default GasStationsTable;
